import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import styles from "./voiceAnalysisInPopup.module.css";
import { tWithKeyAttr as t } from "../../i18nHelper";
import i18n from "../../i18n";

interface VoiceAnalysisProps {
  data: {
    [key: string]: any;
  };
  onRetry?: () => void;
  isRetrying?: boolean;
}

const VoiceAnalysisInPopup: React.FC<VoiceAnalysisProps> = ({ data, onRetry, isRetrying }) => {
  if (
    data == undefined ||
    Object.keys(data).length === 0 ||
    data.toString() == "{}"
  ) {
    return (
      <div className={styles.noInformationContainer}>
        <p className={styles.noInformation}>{t("NO_INFORMATION_WAS_FOUND")}</p>
        {onRetry && (
          <button
            className={styles.retryStageBtn}
            onClick={onRetry}
            disabled={isRetrying}
          >
            {isRetrying ? (
              <><span className={styles.retrySpinner} /> {t("RETRYING")}</>
            ) : (
              <>{t("RETRY_VOICE_ANALYSIS")}</>
            )}
          </button>
        )}
      </div>
    );
  }

  // Check for queued status
  if (data?.message) {
    return (
      <div className={styles.noInformationContainer}>
        <p className={styles.noInformation}>{data.message}</p>
        {onRetry && (
          <button className={styles.retryStageBtn} onClick={onRetry} disabled={isRetrying}>
            {isRetrying ? <><span className={styles.retrySpinner} /> {t("RETRYING")}</> : <>{t("RETRY_VOICE_ANALYSIS")}</>}
          </button>
        )}
      </div>
    );
  }

  // Check for error response
  if (data?.error) {
    return (
      <div className={styles.noInformationContainer}>
        <p className={styles.noInformation}>Error: {data.error}</p>
        {onRetry && (
          <button className={styles.retryStageBtn} onClick={onRetry} disabled={isRetrying}>
            {isRetrying ? <><span className={styles.retrySpinner} /> {t("RETRYING")}</> : <>{t("RETRY_VOICE_ANALYSIS")}</>}
          </button>
        )}
      </div>
    );
  }

  // Check if this is the new enhanced format with emotions wrapper
  const emotionsData = data.emotions || data;
  const statisticsData = data.statistics;
  const insightsData = data.insights;

  const groups = Object.keys(emotionsData).filter((k) => k.startsWith("group"));

  // If data has groups but no overall/stress, calculate them from grouped data
  if (groups.length > 0 && (!emotionsData.overall || !emotionsData.stress)) {
    const positiveEmotions = [
      "Admiration", "Amusement", "Approval", "Caring", "Desire",
      "Excitement", "Gratitude", "Joy", "Love", "Optimism",
      "Pride", "Relief",
    ];
    const negativeEmotions = [
      "Anger", "Annoyance", "Disappointment", "Disapproval", "Disgust",
      "Embarrassment", "Fear", "Grief", "Nervousness", "Remorse", "Sadness",
    ];

    let totalPositive = 0;
    let totalNegative = 0;

    groups.forEach((groupKey) => {
      const group = emotionsData[groupKey];
      Object.entries(group).forEach(([emotion, value]) => {
        const numValue = typeof value === "number" ? value : Number(value);
        if (!isNaN(numValue)) {
          if (positiveEmotions.includes(emotion)) {
            totalPositive += numValue;
          } else if (negativeEmotions.includes(emotion)) {
            totalNegative += numValue;
          }
        }
      });
    });

    const total = totalPositive + totalNegative;
    emotionsData.overall = {
      positive: total > 0 ? Math.round((totalPositive / total) * 100) : 50,
      negative: total > 0 ? Math.round((totalNegative / total) * 100) : 50,
    };
    emotionsData.stress = total > 0 ? Math.round((totalNegative / total) * 100) : 0;
  }

  // Final validation after calculation
  if (
    !emotionsData.overall ||
    typeof emotionsData.overall.positive === "undefined" ||
    typeof emotionsData.overall.negative === "undefined"
  ) {
    return (
      <div className={styles.noInformationContainer}>
        <p className={styles.noInformation}>{t("NO_INFORMATION_WAS_FOUND")}</p>
        {onRetry && (
          <button className={styles.retryStageBtn} onClick={onRetry} disabled={isRetrying}>
            {isRetrying ? <><span className={styles.retrySpinner} /> {t("RETRYING")}</> : <>{t("RETRY_VOICE_ANALYSIS")}</>}
          </button>
        )}
      </div>
    );
  }

  // Calculate top emotions for charts
  const emotionSums: Record<string, number> = {};
  groups.forEach((g) => {
    Object.entries(emotionsData[g]).forEach(([emotion, value]) => {
      if (!emotionSums[emotion]) emotionSums[emotion] = 0;
      emotionSums[emotion] += value as number;
    });
  });

  const top3 = Object.entries(emotionSums)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([emotion]) => emotion);

  const lineChartData = groups.map((g, idx) => ({
    name: `Group ${idx + 1}`,
    ...emotionsData[g],
  }));

  // Histogram data - top 6 emotions
  const histogramData = Object.keys(emotionSums)
    .map((emotion) => ({
      emotion,
      value: emotionSums[emotion],
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 6);

  // Calculate sentiment for display
  const positivePercent = emotionsData.overall.positive;
  const negativePercent = emotionsData.overall.negative;
  const neutralPercent = Math.max(0, 100 - positivePercent - negativePercent);

  return (
    <article className={styles.article}>
      <h3 className={styles.mainTitle}>🎵 {t("AUDIO_ANALYSIS_ADVANCED") || "ניתוח אודיו מתקדם"}</h3>

      {/* Statistics Cards Grid */}
      {statisticsData && (
        <div className={styles.statsGrid}>
          {/* Speaking Time / Speaker Ratio */}
          <div className={styles.statsCard}>
            <div className={styles.statsCardHeader}>
              <div className={styles.statsIcon}>🗣️</div>
              <div className={styles.statsLabel}>{t("SPEAKING_TIME") || "זמן דיבור"}</div>
            </div>
            {statisticsData.speaker_ratio && Object.keys(statisticsData.speaker_ratio).length > 0 ? (
              <div className={styles.speakerRatioContainer}>
                {Object.entries(statisticsData.speaker_ratio).map(([speaker, ratio], idx) => (
                  <div key={idx} className={styles.progressBarContainer}>
                    <div className={styles.progressLabel}>
                      <span>{speaker}</span>
                      <span>{ratio as number}%</span>
                    </div>
                    <div className={styles.progressBar}>
                      <div className={styles.progressFill} style={{ width: `${ratio}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.statsValue}>—</div>
            )}
          </div>

          {/* Speech Rate */}
          <div className={styles.statsCard}>
            <div className={styles.statsCardHeader}>
              <div className={styles.statsIcon}>⚡</div>
              <div className={styles.statsLabel}>{t("SPEECH_RATE") || "קצב דיבור"}</div>
            </div>
            <div className={styles.statsValue}>{statisticsData.speech_rate || 0}</div>
            <div className={styles.statsSubtitle}>{t("WORDS_PER_MINUTE") || "מילים לדקה (ממוצע)"}</div>
            <div className={styles.progressBarContainer}>
              <div className={styles.progressLabel}>
                <span>{t("SLOW") || "איטי"}</span>
                <span>{t("FAST") || "מהיר"}</span>
              </div>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${Math.min(100, (statisticsData.speech_rate / 200) * 100)}%` }}
                />
              </div>
            </div>
          </div>

          {/* Duration */}
          <div className={styles.statsCard}>
            <div className={styles.statsCardHeader}>
              <div className={styles.statsIcon}>⏱️</div>
              <div className={styles.statsLabel}>{t("DURATION") || "משך"}</div>
            </div>
            <div className={styles.statsValue}>
              {Math.floor((statisticsData.duration_seconds || 0) / 60)}:{String((statisticsData.duration_seconds || 0) % 60).padStart(2, '0')}
            </div>
            <div className={styles.statsSubtitle}>{t("MINUTES") || "דקות"}</div>
          </div>

          {/* Pauses */}
          <div className={styles.statsCard}>
            <div className={styles.statsCardHeader}>
              <div className={styles.statsIcon}>⏸️</div>
              <div className={styles.statsLabel}>{t("PAUSES") || "הפסקות"}</div>
            </div>
            <div className={styles.statsValue}>{statisticsData.pause_count || 0}</div>
            <div className={styles.statsSubtitle}>{t("PAUSES_IN_CONVERSATION") || "הפסקות בשיחה"}</div>
          </div>

          {/* Emotional Tone */}
          {insightsData?.emotional_tone && (
            <div className={styles.statsCard}>
              <div className={styles.statsCardHeader}>
                <div className={styles.statsIcon}>🎭</div>
                <div className={styles.statsLabel}>{t("EMOTIONAL_TONE") || "טון רגשי"}</div>
              </div>
              <div className={styles.statsValue}>{insightsData.emotional_tone}</div>
              <div className={styles.statsSubtitle}>{t("OVERALL_ATMOSPHERE") || "אווירה כללית"}</div>
            </div>
          )}

          {/* Energy Level */}
          {insightsData?.energy_level && (
            <div className={styles.statsCard}>
              <div className={styles.statsCardHeader}>
                <div className={styles.statsIcon}>📈</div>
                <div className={styles.statsLabel}>{t("ENERGY_LEVEL") || "רמת אנרגיה"}</div>
              </div>
              <div className={styles.statsValue}>{insightsData.energy_level}</div>
              <div className={styles.statsSubtitle}>{t("ENGAGEMENT_LEVEL") || "רמת מעורבות"}</div>
            </div>
          )}
        </div>
      )}

      {/* Vocal Sentiment Analysis */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>😊 {t("VOCAL_SENTIMENT_ANALYSIS") || "ניתוח סנטימנט קולי"}</h4>
        <div className={styles.sentimentIndicator}>
          <div className={styles.sentimentItem}>
            <div className={styles.sentimentEmoji}>😊</div>
            <div className={styles.sentimentLabel}>{t("POSITIVE") || "חיובי"}</div>
            <div className={styles.sentimentValue}>{positivePercent}%</div>
          </div>
          <div className={styles.sentimentItem}>
            <div className={styles.sentimentEmoji}>😐</div>
            <div className={styles.sentimentLabel}>{t("NEUTRAL") || "נייטרלי"}</div>
            <div className={styles.sentimentValue}>{neutralPercent}%</div>
          </div>
          <div className={styles.sentimentItem}>
            <div className={styles.sentimentEmoji}>😟</div>
            <div className={styles.sentimentLabel}>{t("NEGATIVE") || "שלילי"}</div>
            <div className={styles.sentimentValue}>{negativePercent}%</div>
          </div>
        </div>
      </div>

      {/* Acoustic Insights */}
      {insightsData?.acoustic_insights && insightsData.acoustic_insights.length > 0 && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>💡 {t("ACOUSTIC_INSIGHTS") || "תובנות אקוסטיות"}</h4>
          <ul className={styles.insightsList}>
            {insightsData.acoustic_insights.map((insight: any, index: number) => (
              <li key={index} className={styles.insightItem}>
                <div className={styles.insightIcon}>{insight.icon || "💡"}</div>
                <div className={styles.insightContent}>
                  <div className={styles.insightTitle}>{insight.title}</div>
                  <div className={styles.insightText}>{insight.text}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Anxiety Indicators */}
      {insightsData?.anxiety_indicators && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>🔒 {t("ANXIETY_INDICATORS") || "סימני חרדה"}</h4>
          <div className={styles.anxietyCard}>
            <div className={styles.anxietyLevel} data-level={insightsData.anxiety_indicators.level?.toLowerCase()}>
              {insightsData.anxiety_indicators.level}
            </div>
            <p className={styles.anxietyDescription}>{insightsData.anxiety_indicators.description}</p>
          </div>
        </div>
      )}

      {/* Communication Quality */}
      {insightsData?.communication_quality && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>📞 {t("COMMUNICATION_QUALITY") || "איכות תקשורת"}</h4>
          <div className={styles.qualityBox}>
            {insightsData.communication_quality}
          </div>
        </div>
      )}

      {/* Emotions Charts Section */}
      {groups.length > 0 && (
        <div className={styles.chartsSection}>
          <h4 className={styles.sectionTitle}>📊 {t("EMOTION_ANALYSIS") || "ניתוח רגשות"}</h4>
          <div className={styles.chartsGrid}>
            {/* Emotions Over Time */}
            <div className={styles.chartCard}>
              <h5 className={styles.chartTitle}>{t("EMOTIONS_OVER_TIME") || "רגשות לאורך זמן"}</h5>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={lineChartData} margin={{ right: 70 }}>
                  <XAxis
                    dataKey="name"
                    tickFormatter={(_, index) => {
                      const seconds = 5 + index * 18;
                      const minutes = Math.floor(seconds / 60);
                      const secs = seconds % 60;
                      return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
                    }}
                    tick={{ dy: 10, fontSize: 12, fill: "#495057" }}
                    stroke="#adb5bd"
                  />
                  <YAxis tick={false} stroke="#adb5bd" />
                  <Tooltip />
                  <Legend
                    layout="vertical"
                    align="left"
                    verticalAlign="middle"
                    wrapperStyle={{ fontSize: "13px", color: "#495057" }}
                  />
                  {top3.map((emotion, idx) => (
                    <Line
                      key={emotion}
                      type="monotone"
                      dataKey={emotion}
                      stroke={["#6b5ce7", "#f59e0b", "#14b8a6"][idx]}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Emotions Histogram */}
            <div className={styles.chartCard}>
              <h5 className={styles.chartTitle}>{t("TOP_EMOTIONS") || "רגשות מובילים"}</h5>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart
                  layout="vertical"
                  data={histogramData}
                  margin={{ top: 5, right: 10, bottom: 5, left: 5 }}
                >
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="emotion"
                    width={75}
                    tick={{ fontSize: 12, fill: "#495057" }}
                    interval={0}
                    stroke="#adb5bd"
                  />
                  <Tooltip />
                  <Bar dataKey="value" fill="#6b5ce7" barSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Stress Level */}
      {emotionsData.stress !== undefined && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>😰 {t("STRESS_LEVEL") || "רמת לחץ"}</h4>
          <div className={styles.stressContainer}>
            <div className={styles.stressBarWrapper}>
              <div className={styles.stressLabels}>
                <span>{t("LOW") || "נמוך"}</span>
                <span>{t("HIGH") || "גבוה"}</span>
              </div>
              <div className={styles.stressBarOuter}>
                <div
                  className={styles.stressBarInner}
                  style={{ 
                    width: `${emotionsData.stress}%`,
                    backgroundSize: `${100 / (emotionsData.stress / 100)}% 100%`,
                    backgroundImage: i18n.dir() === 'rtl' 
                      ? 'linear-gradient(90deg, #ef4444 0%, #fbbf24 50%, #4ade80 100%)'
                      : 'linear-gradient(90deg, #4ade80 0%, #fbbf24 50%, #ef4444 100%)',
                    backgroundPosition: i18n.dir() === 'rtl' ? 'right' : 'left',
                    marginLeft: i18n.dir() === 'rtl' ? 'auto' : undefined
                  }}
                />
              </div>
              <div className={styles.stressValue}>{emotionsData.stress}%</div>
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

export default VoiceAnalysisInPopup;
